import { Cart } from '../cart/cart';
import { CartItem } from '../cart/cart-item';
import { CheckoutBillingInfo } from '../checkout/billingInfo';
import { CheckoutContactInfo } from '../checkout/contactInfo';
import { CheckoutPremiumInfo } from '../checkout/premium';
import { AddressType } from '../shared/address';
import { ErrorBase } from '../shared/errors';
import { BlackList } from './blackList';

/* eslint-disable no-use-before-define */
export interface BlacklistPayload {
  Address: string;
  CartID: string;
  Email: string;
  FirstName: string;
  LastName: string;
  Phone: string;
  SupporterIP: string;
}

/**
 * Upstream transaction payload body
 */
export interface TransactionRequest {
  AnalyticsClientID: string | null;
  Comments: string | null;
  IPAddress: string | null;
  IsLoggedIn: boolean;
  IsPremiumRequested: boolean;
  PaymentMethodList: TransactionPaymentMethod[];
  ReferrerUrl: string | null;
  RequestDateTime: string;
  RequestTimezone: string;
  SourceCode: string | null;
  SourceCodeOtherDescription?: string;
  SourceCodePremium: string | null;
  SourceSystem: SourceSystem;
  SupporterInformation: TransactionSupporter;
  TransactionID: string | null;
  TransactionItemList: TransactionItem[];
  /** These perceptive values are commented out in legacy, are they still needed? */
  // Perceptive_ImageURL: string | null;
  // Perceptive_TemplateID: string | null;
}

export enum SourceSystem {
  DotCom = '.Com',
  ExternalCallCenter = 'External Call Center',
}

export interface TransactionItem {
  Beneficiary_GlobalID: string | null;
  Beneficiary_HoldID: string | null;
  BeneficiaryCriteria: TransactionChildCriteria | null;
  Commitment_ID: string | null;
  CommitmentAction: CommitmentAction;
  CurrentRate: number;
  Frequency: PaymentFrequencyType;
  FundCode: string | null;
  FuturePullDate?: string;
  FutureRate?: number;
  FutureRateStartDate?: Date;
  ICP_ID: string | null; // ChurchPartnerId; only for CenterGift
  ImmediatePull?: 'YES' | 'NO';
  Intervention_ID: string | null;
  IsRecurring: boolean;
  PaymentList: TransactionPaymentLink[];
  PaymentPlan: PaymentPlanBase | null;
  cartItem?: CartItem;
}

export interface TransactionAnalyticsItem {
  BeneficiaryBirthday: string | null;
  BeneficiaryCenterId: string | null;
  BeneficiaryCountry: string | null;
  BeneOrCustomId: string | null;
  BeneficiaryImageUrl: string | null;
  BeneficiaryName: string | null;
  CartItemGroup: string | null;
  CmsId: string | null;
  CurrentRate: number;
  Frequency: string | null;
  FundCode: string | null;
  InterventionGlobalId: string | null;
  PaymentPlanCreated: boolean;
  Quantity: number;
}

export interface TransactionAnalyticsRequest {
  AnalyticsClientId: string | null;
  CartId: string | null;
  FirstName: string | null;
  IsBlacklisted: boolean;
  Items: TransactionAnalyticsItem[];
  PaymentType: string | null;
  Referrer: string | null;
  SourceCode: string | null;
  SourceCodePremium: string | null;
  State: string | null;
  SupporterGlobalId: string | null;
  Time: string | null;
  TransactionId: string | null;
}

export interface TransactionData {
  billingInfo: CheckoutBillingInfo;
  blacklist: BlackList | null;
  cart: Cart;
  contactInfo: CheckoutContactInfo;
  premium: CheckoutPremiumInfo | null;
  sourceCode: string;
  supporterId?: string;
  contactId?: string;
  completedCarts?: string[];
}

export interface TransactionClientData {
  analyticsClientID?: string;
  comments?: string;
  ipAddress: string;
  isLoggedIn: boolean;
  referrerUrl: string;
  requestDateTime: string;
  requestTimezone: string;
}

export interface TransactionSubmitRequest {
  processCount: number;
  cartId: Cart['id'];
  transaction: {
    transactionData: TransactionData;
    transactionClientData: TransactionClientData;
  };
}

export interface TransactionPaymentLink {
  Amount: number | null;
  PaymentMethodIndex: number; // basically always 0
}

export interface PaymentPlanBase {
  PaymentFrequency: PaymentFrequencyType;
  /**
   * if we have shown the user an option for a payment day, we send that number as
   * a string. if not, just send a null. however, PaymentPullDayOfMonth *cannot* be
   * null for an ACH transaction.
   */
  PaymentPullDayOfMonth: string | null; // ie. '13'. rarely sent.
}

export interface TransactionChildCriteria {
  CountryList: string[];
  Gender: string | null; // Gender? enum toString
  IsHivAffectedArea?: boolean | null;
  IsOrphan?: boolean | null;
  IsSpecialNeeds?: boolean | null;
  MaximumAge: number | null;
  MinimumAge: number | null;
  Quantity: number | null;
}

export enum CommitmentAction {
  CreateCommitment = 'Insert',
  OneTime = 'One-Time',
  UpdateCommitment = 'Update',
}

export enum PaymentFrequencyType {
  NA = 'NA',
  Annually = 'Annually',
  Monthly = 'Monthly',
  OneTime = 'One-Time',
  Quarterly = 'Quarterly',
  Semiannually = 'Semi-Annually',
}

export type TransactionPaymentMethod = {
  // PaymentItemType: TransactionPaymentTypeEnum;
  ACHAccount: AchAccount | null;
  BillingAddress: AddressBase | null;
  CreditCard: CreditCard | CreditCardApplePay | null;
  PayPal: PayPal | null;
  Nickname: string | null;
  PaymentMethodID: string | null;
  PaymentMethodIndex: number;
  SavePaymentMethod: boolean;
};

/**
 * NOTE: This is the logic to break TransactionPaymentMethod out into
 * a discriminated union type to get better intellisense for building up
 * payment methods if/when we have time to revisit.
 */
// export type TransactionPaymentMethod = TransactionPaymentBase &
//   (
//     | PaymentMethodACH
//     | PaymentMethodCreditCard
//     | PaymentMethodPayPal
//     | PaymentMethodApplePay
//   );
//
// export interface TransactionPaymentBase {
//   // PaymentItemType: TransactionPaymentTypeEnum.NA;
//   // ACHAccount: null;
//   // BillingAddress: null;
//   // CreditCard: null;
//   // PayPal: null;
//   Nickname: string | null;
//   PaymentMethodID: string | null;
//   PaymentMethodIndex: number;
//   // SavePaymentMethod: false;
// }
// interface PaymentMethodCreditCard {
//   BillingAddress: AddressBase;
//   CreditCard: CreditCard;
//   PayPal: null;
//   PaymentItemType: TransactionPaymentTypeEnum.CreditCard;
//   SavePaymentMethod: boolean;
// }
// interface PaymentMethodPayPal {
//   BillingAddress: null;
//   CreditCard: null;
//   PayPal: PayPal;
//   PaymentItemType: TransactionPaymentTypeEnum.PayPal;
// }
// interface PaymentMethodACH {
//   ACHAccount: AchAccount;
//   BillingAddress: null;
//   CreditCard: null;
//   PayPal: null;
//   PaymentItemType: TransactionPaymentTypeEnum.ACH;
//   SavePaymentMethod: boolean;
// }
// type PaymentMethodApplePay = PaymentMethodCreditCard & {
//   BillingAddress: null;
//   CreditCard: CreditCardApplePay;
//   PayPal: null;
//   PaymentItemType: TransactionPaymentTypeEnum.ApplePay;
// };

export enum TransactionPaymentTypeEnum {
  NA = 'NA',
  ACH = 'ACH',
  ApplePay = 'ApplePay',
  CreditCard = 'CreditCard',
  PayPal = 'PayPal',
}

export interface AchAccount {
  AccountLastFour: string | null;
  AccountName: string;
  AccountNumber: string;
  RoutingNumber: string;
}

export interface CreditCardBase {
  AuthorizationCode?: string | null;
  AVSResult: AVSResult | null;
  CardType: CreditCardType;
  // DPMAuthDetails: string | null;
  // DPMType: DPMType | null;
  ExpirationMonthYear: string | null; // made up of ExpirationMonth/ExpirationYear  [Range(1, 12)]/[Range(2000, 2030)]
  MerchantOrderNumber: string | null;
  NameOnCard: string;
  ProcessorResponseCode: string | null;
  ProcessorResponseDate: string | null;
  Token: string;
  TransactionType: number;
}

export type CreditCard = CreditCardBase & {
  CVVResult: CVVResult | null;
};

export type CreditCardApplePay = CreditCardBase & {
  DPMType: DPMType;
  DPMAuthDetails: string;
};

export enum DPMType {
  ApplePay = 'Apple Pay',
}

export interface AVSResult {
  Code: string | null;
  Message: string | null;
  PostalMatch: string | null;
  StreetMatch: string | null;
}

export interface CVVResult {
  Code: string | null;
  Message: string | null;
}

export interface PayPal {
  PayPalAccountEmail: string | null; // AccountEmail
  PayPalAuthorizationID: string | null; // AuthorizationId
  PayPalPayerId: string | null; // PayerId
  Token: string | null;
}

export enum CreditCardType {
  NA = 'NA',
  AMEX = 'American Express',
  DISC = 'Discover',
  MC = 'Master Card',
  VISA = 'Visa',
}

export interface TransactionSupporter {
  AddressList: AddressBase[];
  BlacklistItems: BlacklistItem[] | null;
  // CompassConstituentID: string | null; // LegacyId [TODO: Do we still need this?]
  EmailList: Email[];
  FirstName: string;
  Gender?: 'M' | 'F' | null;
  LastName: string;
  OrganizationName: string;
  PhoneList: PhoneNumber[]; // sets and uses SupporterPhoneNumbers
  Supporter_GlobalID: string; // Sets and uses SupporterGlobalId we don't need both but maybe just this. See TODO for Protected
  SupporterType_Name: string; // Sets and uses SupporterType we don't need both but maybe just this. See TODO for Protected
  // TODO: if protected does this keep this property from being parsed?
}

export interface BlacklistItem {
  ID: string;
  Description: string;
}

export interface Email {
  EmailAddress: string; // (EmailId) Required
  EmailAddressType: EmailType; // Required
  IsPreferredEmailAddress: boolean; // string;
  IsUndeliverable: boolean;
}

export enum EmailType {
  Business = 'Business',
  Personal = 'Personal',
  Work = 'Work',
}

export interface PhoneNumber {
  IsPreferredPhoneNumber: boolean;
  IsUndeliverable: boolean;
  PhoneNumber: string;
  PhoneNumberType: PhoneNumberType;
}

export enum PhoneNumberType {
  Business = 'Business',
  Home = 'Home',
  Mobile = 'Mobile',
  Work = 'Work',
}

export interface AddressBase {
  // AddressLine1/2 are not used in SF for any of the addresses - including them in case SF or TO wants the discrete values...
  // AddressLine1: string;
  // AddressLine2?: string;
  AddressType: AddressType;
  City: string;
  Country: string;
  CountryDivision: string;
  PostalCode: string;
  Status?: AddressStatus;
  // SF expects the "Street" field to contain the \n-delimited line 1 and line 2...
  Street: string;
  // InCareOfAddressLine?: string | null;
}

export enum AddressStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Undeliverable = 'Undeliverable',
}

export interface Transaction {
  success: boolean | undefined;
  transactionId?: string;
  errors?: ErrorBase[];
}

export interface TokenExTransaction {
  Error: string;
  ReferenceNumber: string;
  Success: boolean;
  AVS_Result: TokenExAvsResult;
  Authorization: string;
  CVV_Result: TokenExCvvResult;
  Message: string;
  Params: TokenExTransactionParam[];
  Test: boolean;
  TransactionResult: boolean;
}

interface TokenExAvsResult {
  Code: string;
  Message: string;
  PostalMatch: string;
  StreetMatch: string;
}

interface TokenExCvvResult {
  Code: string;
  Message: string;
}

interface TokenExTransactionParam {
  Key: string;
  Value: string;
}
